/**
 * Utility Functions
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format number with compact notation
 */
export function formatCompact(value: number | null | undefined): string {
  const v = value ?? 0;
  if (Math.abs(v) >= 1e9) {
    return `${(v / 1e9).toFixed(2)}B`;
  }
  if (Math.abs(v) >= 1e6) {
    return `${(v / 1e6).toFixed(2)}M`;
  }
  if (Math.abs(v) >= 1e3) {
    return `${(v / 1e3).toFixed(2)}K`;
  }
  return v.toFixed(2);
}

/**
 * Format USD value
 */
export function formatUSD(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format price with appropriate decimals
 */
export function formatPrice(price: number | null | undefined, _asset?: string): string {
  if (!price) return '$0.00';

  // More decimals for smaller prices
  if (price < 1) {
    return `$${price.toFixed(4)}`;
  }
  if (price < 100) {
    return `$${price.toFixed(2)}`;
  }
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format percentage
 */
export function formatPercent(value: number | null | undefined, decimals = 2): string {
  const v = value ?? 0;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(decimals)}%`;
}

/**
 * Format relative time
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  if (seconds > 10) return `${seconds}s ago`;
  return 'just now';
}

/**
 * Format timestamp
 */
export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Get direction color class
 */
export function getDirectionColor(direction: 'LONG' | 'SHORT' | 'buy' | 'sell' | string): string {
  const normalized = direction.toLowerCase();
  const bullish = ['long', 'buy', 'bullish', 'up'].includes(normalized);
  return bullish ? 'text-bullish' : 'text-bearish';
}

/**
 * Get direction background class
 */
export function getDirectionBg(direction: 'LONG' | 'SHORT' | 'buy' | 'sell' | string): string {
  const normalized = direction.toLowerCase();
  const bullish = ['long', 'buy', 'bullish', 'up'].includes(normalized);
  return bullish ? 'bg-bullish' : 'bg-bearish';
}
